import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useController, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePrompt } from '@/src/hooks/usePrompt';
import ImageUpload from '@/src/components/atom/ImageUpload';
import { useRef, useState } from 'react';
import IconButton from '@/src/components/atom/Button/IconButton';
import Select from '@/src/components/atom/Select';
import { urlRegex } from '@/src/utils/regex';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { PostHomeContentRequestBody } from '@/src/apis/rest/homeContent.dtos';
import {
  getHomeContentInAppLinkCatetoryAPI,
  postHomeContentAPI,
} from '@/src/apis/rest/homeContent';

const BACK_PATH = PATH.APP_HOME_CONTENT_LIST;

const formSchema = () => {
  return z.object({
    contentType: z.string(),
    title: z.string().min(3),
    isExposed: z.boolean(),
    isExposeNewBadge: z.boolean(),
    content: z.string().min(1),
    contentMediaId: z.number(),
  });
};

const DEFAULT_VALUES: Partial<PostHomeContentRequestBody> = {
  contentType: 'IN_APP_PAGE',
  title: '',
  isExposed: true,
  isExposeNewBadge: true,
};

export default function AppHomeContentCreatePage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();

  // state
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);
  const [isLinkError, setIsLinkError] = useState(false);

  // query
  const { data: categoryOptions } = useQuery({
    queryKey: ['home-content-in-app-link-category-options'],
    queryFn: getHomeContentInAppLinkCatetoryAPI,
    select: data =>
      (data?.rows || []).map(item => ({ label: item.key, value: item.value })),
  });

  // form
  const form = useForm<PostHomeContentRequestBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
  });
  const contentController = useController({
    name: 'content',
    control: form.control,
  });

  // mutation
  const postHomeContentMutation = useMutation({
    mutationFn: postHomeContentAPI,
  });

  // event
  const handleSubmit = (values: any) => {
    const newValues = { ...values };

    postHomeContentMutation.mutate(newValues, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: '성공',
          description: '성공적으로 저장되었습니다.',
        });

        navigate(BACK_PATH);
      },
      onError: e => {
        toast({
          variant: 'error',
          title: '실패',
          description: e.message,
        });
      },
    });
  };

  const getIsContentValid = () => {
    switch (form.watch().contentType) {
      case 'EXTERNAL_LINK':
        return (
          !!form.watch().content && urlRegex.test(form.watch().content || '')
        );
      default:
        return true;
    }
  };

  const handleResetContent = () => {
    contentController.field.onChange('');
    setIsLinkError(false);
  };

  // render
  const renderConnectContent = () => {
    switch (form.watch('contentType')) {
      case 'IN_APP_PAGE':
        return (
          <FormField
            control={form.control}
            name="content"
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <Select
                    rootProps={{
                      value: field?.value || undefined,
                      onValueChange: field.onChange,
                    }}
                    valueProps={{
                      placeholder: '연결할 앱 내 화면을 선택해주세요',
                    }}
                    option={[
                      {
                        optionList: categoryOptions || [],
                      },
                    ]}
                  />
                </FormContent>
              );
            }}
          />
        );
      case 'EXTERNAL_LINK':
        return (
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => {
              return (
                <FormContent
                  isRequired
                  label="연결 컨텐츠"
                  error={
                    isLinkError
                      ? {
                          type: 'pattern',
                          message:
                            'url 형식을 확인해주세요 (https://example.com)',
                        }
                      : undefined
                  }
                >
                  <Input
                    value={field?.value || undefined}
                    onChange={e => {
                      field.onChange(e.target.value);

                      if (e.target.value) {
                        setIsLinkError(!urlRegex.test(e.target.value));
                      } else {
                        setIsLinkError(false);
                      }
                    }}
                    onClear={() => {
                      field.onChange('');
                      setIsLinkError(false);
                      setTimeout(() => {
                        field.onChange(undefined);
                      }, 0);
                    }}
                    containerClassName="flex-1"
                    placeholder="링크를 입력해주세요 (https://example.com)"
                  />
                </FormContent>
              );
            }}
          />
        );
      default:
        return (
          <FormField
            control={form.control}
            name="content"
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <FormContent isRequired label="연결 컨텐츠" error={error}>
                  <Select
                    rootProps={{
                      value: field?.value || undefined,
                      onValueChange: field.onChange,
                    }}
                    valueProps={{
                      placeholder: '연결할 앱 내 화면을 선택해주세요',
                    }}
                    option={[
                      {
                        optionList: categoryOptions || [],
                      },
                    ]}
                  />
                </FormContent>
              );
            }}
          />
        );
    }
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(-1)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              이전 화면
            </div>
          </Button>
          <Button
            disabled={!form.formState.isValid || !getIsContentValid()}
            type="button"
            onClick={openSaveDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={postHomeContentMutation.isPending}
          >
            <div className="flex items-center gap-x-2">저장</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  // usePrompt
  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  return (
    <>
      <MainContentTitle
        breadcrumbList={['홈 퀵메뉴 관리', '홈 퀵메뉴 등록']}
        pageTitle="홈 퀵메뉴 등록"
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px]"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="홈 퀵메뉴 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-4">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="제목" isRequired error={error}>
                        <Input
                          {...field}
                          containerClassName="flex-1"
                          placeholder="제목을 입력해주세요 (3~5자로 설정 가능합니다)"
                          maxLength={5}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="isExposed"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            { label: '노출', value: 'true' },
                            { label: '미노출', value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: (e: string) =>
                              field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="contentMediaId"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="아이콘 이미지"
                        isRequired
                        error={error}
                        defaultMessage={
                          <ImageUploadInfo widthSize={44} heightSize={44} />
                        }
                      >
                        <ImageUpload
                          width={140}
                          height={140}
                          widthText={44}
                          heightText={44}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          onChange={e => {
                            field.onChange(e.id);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="isExposeNewBadge"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="신규 뱃지 알림 여부"
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            {
                              label: '알림 노출',
                              value: 'true',
                            },
                            {
                              label: '알림 미노출',
                              value: 'false',
                            },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: (e: string) =>
                              field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="contentType"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="컨텐츠 유형 선택"
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            {
                              label: '앱 내 화면',
                              value: 'IN_APP_PAGE',
                            },
                            {
                              label: '외부 링크 연결',
                              value: 'EXTERNAL_LINK',
                            },
                          ]}
                          rootProps={{
                            value: field.value,
                            onValueChange: e => {
                              field.onChange(e);
                              handleResetContent();
                            },
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                {renderConnectContent()}
              </div>
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* 모달 */}
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title="작성된 내용을 등록하시겠어요?"
          isLoading={postHomeContentMutation.isPending}
        />
      )}
    </>
  );
}
